import { Cadence } from './plans';
import { UPGRADE_FEATURE_PARAM, UPGRADE_ORIGIN_PARAM, UpgradeOrigin } from './upgrade-origin';

/**
 * The checkout screen's own route, and where its Back button goes.
 *
 * Checkout used to be a modal over the picker, so "back" was closing it and the picker was still
 * mounted underneath. As a page it has to say where it came from, and that is not always the picker:
 * a top-up deeplink (`settings_topup` / `sidebar_topup` carry `topUpPackageId`) opens checkout the
 * moment the picker loads, so a buyer arriving that way has never *seen* the picker. Sending them
 * "back" to it would land them somewhere new, being asked to choose something they already chose.
 *
 * So the CALLER states the destination rather than this module inferring it. The picker knows
 * whether it auto-opened; nothing downstream can tell.
 */
export type CheckoutBack = 'picker' | 'origin';

/** What the checkout screen is buying — the picker's target, carried through the URL. */
export interface CheckoutRouteTarget {
  agentPlanId?: string;
  packId?: string;
  cadence: Cadence;
  couponCode?: string;
}

export const CHECKOUT_PATH = '/checkout';
export const CHECKOUT_BACK_PARAM = 'back';

/**
 * Where each origin's buyer belongs when checkout was reached WITHOUT the picker in between.
 *
 * Only the surfaces that can deeplink straight into checkout need an entry; everything else goes
 * back to the picker and never consults this. An origin we do not know maps to the picker too —
 * a wrong guess would strand someone on a page they never came from, and the picker is at least a
 * place the purchase can be restarted.
 */
const ORIGIN_HOME: Partial<Record<UpgradeOrigin, string>> = {
  settings_topup: '/settings',
  settings_upgrade: '/settings',
  settings_switch_plan: '/settings',
  settings_get_more: '/settings',
  sidebar_topup: '/',
  sidebar_upgrade: '/',
  chat_credits_card: '/',
  brief_upgrade: '/',
};

function withBase(basePath: string, path: string): string {
  return `${basePath}${path}`;
}

/** The href that opens checkout for this target. */
export function checkoutHref(
  basePath: string,
  target: CheckoutRouteTarget,
  opts: { origin?: string; feature?: string; back: CheckoutBack },
): string {
  const params = new URLSearchParams();
  if (target.agentPlanId) params.set('agentPlanId', target.agentPlanId);
  if (target.packId) params.set('packId', target.packId);
  params.set('cadence', target.cadence);
  if (target.couponCode) params.set('couponCode', target.couponCode);
  if (opts.origin) params.set(UPGRADE_ORIGIN_PARAM, opts.origin);
  // The feature the upgrade was opened for (docs/compliance/bi.md §2) — the checkout page's view carries it.
  if (opts.feature) params.set(UPGRADE_FEATURE_PARAM, opts.feature);
  params.set(CHECKOUT_BACK_PARAM, opts.back);
  return `${withBase(basePath, CHECKOUT_PATH)}?${params.toString()}`;
}

/** One query value, whatever Next hands us — a repeated param arrives as an array. */
function one(value: string | string[] | undefined): string | undefined {
  const raw = Array.isArray(value) ? value[0] : value;
  const trimmed = (raw ?? '').trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * The target out of the route's query, or null when there isn't a usable one.
 *
 * Null rather than a partial target: the screen cannot price "some plan", and rendering a checkout
 * around a missing id would ask the buyer for a card before it knows what it is selling.
 */
export function readCheckoutRouteTarget(query: Record<string, string | string[] | undefined>): CheckoutRouteTarget | null {
  const agentPlanId = one(query.agentPlanId);
  const packId = one(query.packId);
  if (!agentPlanId && !packId) {
    return null;
  }
  // A pack is a one-off. Its cadence is meaningless, and 'monthly' is what the picker sends.
  const cadence: Cadence = one(query.cadence) === 'yearly' ? 'yearly' : 'monthly';
  return {
    ...(agentPlanId ? { agentPlanId } : {}),
    ...(packId ? { packId } : {}),
    cadence,
    ...(one(query.couponCode) ? { couponCode: one(query.couponCode) } : {}),
  };
}

/**
 * Where Back goes from checkout.
 *
 * Driven off the query rather than `history.back()`, and that is not a style preference. The native
 * iOS and Android apps open this URL cold in a browser, and a bank redirect replaces the entry too —
 * in both cases there is no history entry to return to, so `history.back()` either does nothing or
 * leaves the app entirely. A destination we can name always works.
 */
export function checkoutBackHref(basePath: string, query: Record<string, string | string[] | undefined>): string {
  const back = one(query[CHECKOUT_BACK_PARAM]);
  if (back !== 'origin') {
    return withBase(basePath, '/packages');
  }
  const origin = one(query[UPGRADE_ORIGIN_PARAM]) as UpgradeOrigin | undefined;
  return withBase(basePath, (origin && ORIGIN_HOME[origin]) || '/packages');
}
